import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MapPin, Camera, X, Loader2, Plus, Building2, Trash2 } from 'lucide-react';

function ProfileTab({
  photoUrl,
  displayName,
  setDisplayName,
  companyName,
  setCompanyName,
  phone,
  setPhone,
  email,
  setEmail,
  website,
  setWebsite,
  yearsExp,
  setYearsExp,
  responseGuarantee,
  setResponseGuarantee,
  bio,
  setBio,
  addressType,
  setAddressType,
  addressStreet,
  setAddressStreet,
  addressSuite,
  setAddressSuite,
  addressCity,
  setAddressCity,
  addressStateVal,
  setAddressStateVal,
  addressZip,
  setAddressZip,
  satelliteOffices,
  setSatelliteOffices,
  uploadingPhoto,
  handlePhotoUpload,
  handlePhotoDelete,
  updateProfile,
  saving
}) {
  // Satellite office handlers
  var addSatelliteOffice = function() {
    var newOffice = {
      office_id: 'sat_' + Date.now(),
      name: '',
      street: '',
      suite: '',
      city: '',
      state: '',
      zip_code: '',
      phone: '',
      contact_name: ''
    };
    setSatelliteOffices([].concat(satelliteOffices || [], [newOffice]));
  };

  var updateSatelliteOffice = function(officeId, field, value) {
    setSatelliteOffices((satelliteOffices || []).map(function(office) {
      if (office.office_id === officeId) {
        var updated = Object.assign({}, office);
        updated[field] = value;
        return updated;
      }
      return office;
    }));
  };

  var removeSatelliteOffice = function(officeId) {
    setSatelliteOffices((satelliteOffices || []).filter(function(office) {
      return office.office_id !== officeId;
    }));
  };
  return (
    <div className="space-y-6">
      {/* Profile Photo Card */}
      <Card className="card-base">
        <CardHeader><CardTitle className="text-base text-white">Profile Photo</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="relative">
              {photoUrl ? (
                <img src={photoUrl} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 border-slate-700" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-slate-900 text-xl font-bold">
                  {displayName ? displayName.charAt(0).toUpperCase() : 'S'}
                </div>
              )}
              {photoUrl && (
                <button 
                  onClick={handlePhotoDelete}
                  disabled={uploadingPhoto}
                  className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-400 rounded-full p-1 text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="flex-1">
              <p className="text-xs text-slate-400 mb-2">Max 2MB, JPEG/PNG/WebP/GIF</p>
              <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-sm rounded-lg cursor-pointer transition-colors">
                {uploadingPhoto ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
                <span>{photoUrl ? 'Change' : 'Upload'}</span>
                <input 
                  type="file" 
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="card-base">
        <CardHeader><CardTitle className="text-base text-white">Basic Information</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label htmlFor="profile-display-name" className="text-slate-300">Display Name</Label><Input id="profile-display-name" name="display_name" value={displayName} onChange={function(e) { setDisplayName(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" /></div>
            <div><Label htmlFor="profile-company-name" className="text-slate-300">Company Name</Label><Input id="profile-company-name" name="company_name" value={companyName} onChange={function(e) { setCompanyName(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" /></div>
            <div><Label htmlFor="profile-phone" className="text-slate-300">Phone</Label><Input id="profile-phone" name="phone" value={phone} onChange={function(e) { setPhone(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" /></div>
            <div><Label htmlFor="profile-email" className="text-slate-300">Public Email</Label><Input id="profile-email" name="email" type="email" value={email} onChange={function(e) { setEmail(e.target.value); }} placeholder="contact@yourcompany.com" className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" /></div>
            <div><Label htmlFor="profile-website" className="text-slate-300">Website</Label><Input id="profile-website" name="website" value={website} onChange={function(e) { setWebsite(e.target.value); }} placeholder="https://..." className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" /></div>
            <div><Label htmlFor="profile-years-exp" className="text-slate-300">Years of Experience</Label><Input id="profile-years-exp" name="years_experience" type="number" value={yearsExp} onChange={function(e) { setYearsExp(e.target.value); }} className="bg-slate-900/60 border-slate-700 text-white" /></div>
            <div className="md:col-span-2"><Label htmlFor="profile-response-guarantee" className="text-slate-300">Response Guarantee (hours)</Label><Input id="profile-response-guarantee" name="response_guarantee" type="number" value={responseGuarantee} onChange={function(e) { setResponseGuarantee(e.target.value); }} placeholder="e.g., 4" className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" /></div>
          </div>
          <div className="mt-4">
            <Label htmlFor="profile-bio" className="text-slate-300">Bio</Label>
            <Textarea id="profile-bio" name="bio" value={bio} onChange={function(e) { setBio(e.target.value); }} rows={4} className="bg-slate-900/60 border-slate-700 text-white" />
          </div>
        </CardContent>
      </Card>
      
      {/* Your Address Card */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-base text-white flex items-center gap-2">
            <MapPin className="h-4 w-4 text-blue-400" />
            Your Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 mb-4">Your address is displayed on your public profile and used for distance calculations.</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="address-type" className="text-slate-400 text-xs">Address Type</Label>
              <Select value={addressType} onValueChange={setAddressType}>
                <SelectTrigger id="address-type" className="mt-1 bg-slate-900/60 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="business" className="text-slate-300">Business Address</SelectItem>
                  <SelectItem value="home" className="text-slate-300">Home Address</SelectItem>
                  <SelectItem value="po_box" className="text-slate-300">PO Box</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label htmlFor="address-street" className="text-slate-400 text-xs">Street Address</Label>
                <Input id="address-street" name="address_street" value={addressStreet} onChange={function(e) { setAddressStreet(e.target.value); }} placeholder="123 Main St" className="mt-1 bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
              </div>
              <div>
                <Label htmlFor="address-suite" className="text-slate-400 text-xs">Suite / Unit / Box</Label>
                <Input id="address-suite" name="address_suite" value={addressSuite} onChange={function(e) { setAddressSuite(e.target.value); }} placeholder="Ste 100" className="mt-1 bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="address-city" className="text-slate-400 text-xs">City</Label>
                <Input id="address-city" name="address_city" value={addressCity} onChange={function(e) { setAddressCity(e.target.value); }} className="mt-1 bg-slate-900/60 border-slate-700 text-white" />
              </div>
              <div>
                <Label htmlFor="address-state" className="text-slate-400 text-xs">State</Label>
                <Input id="address-state" name="address_state" value={addressStateVal} onChange={function(e) { setAddressStateVal(e.target.value.toUpperCase()); }} maxLength={2} className="mt-1 bg-slate-900/60 border-slate-700 text-white" />
              </div>
              <div>
                <Label htmlFor="address-zip" className="text-slate-400 text-xs">ZIP</Label>
                <Input id="address-zip" name="address_zip" value={addressZip} onChange={function(e) { setAddressZip(e.target.value); }} className="mt-1 bg-slate-900/60 border-slate-700 text-white" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Satellite Offices Card */}
      <Card className="card-base">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Building2 className="h-4 w-4 text-teal-400" />
              Satellite Offices
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={addSatelliteOffice}
              className="bg-slate-800 border-slate-700 text-teal-400 hover:bg-slate-700 hover:text-teal-300"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Office
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 mb-4">
            Add satellite offices to show broader coverage. Distance calculations will use your closest office to the client's location.
          </p>
          
          {(!satelliteOffices || satelliteOffices.length === 0) ? (
            <div className="text-center py-6 text-slate-500 text-sm border border-dashed border-slate-700 rounded-lg">
              No satellite offices added yet. Click "Add Office" to expand your coverage area.
            </div>
          ) : (
            <div className="space-y-4">
              {satelliteOffices.map(function(office, index) {
                return (
                  <div key={office.office_id} className="p-4 bg-slate-900/60 border border-slate-700 rounded-lg relative">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-teal-400">Office #{index + 1}</span>
                      <button
                        onClick={function() { removeSatelliteOffice(office.office_id); }}
                        className="text-red-400 hover:text-red-300 p-1"
                        title="Remove office"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label htmlFor={'sat-office-name-' + office.office_id} className="text-slate-400 text-xs">Office Name (optional)</Label>
                        <Input 
                          id={'sat-office-name-' + office.office_id}
                          name="office_name"
                          value={office.name || ''} 
                          onChange={function(e) { updateSatelliteOffice(office.office_id, 'name', e.target.value); }} 
                          placeholder="e.g., North Office" 
                          className="mt-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                        />
                      </div>
                      <div>
                        <Label htmlFor={'sat-office-street-' + office.office_id} className="text-slate-400 text-xs">Street Address</Label>
                        <Input 
                          id={'sat-office-street-' + office.office_id}
                          name="office_street"
                          value={office.street || ''} 
                          onChange={function(e) { updateSatelliteOffice(office.office_id, 'street', e.target.value); }} 
                          placeholder="456 Oak Ave" 
                          className="mt-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                        />
                      </div>
                      <div>
                        <Label htmlFor={'sat-office-suite-' + office.office_id} className="text-slate-400 text-xs">Suite / Unit</Label>
                        <Input 
                          id={'sat-office-suite-' + office.office_id}
                          name="office_suite"
                          value={office.suite || ''} 
                          onChange={function(e) { updateSatelliteOffice(office.office_id, 'suite', e.target.value); }} 
                          placeholder="Ste 200" 
                          className="mt-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                        />
                      </div>
                      <div>
                        <Label htmlFor={'sat-office-city-' + office.office_id} className="text-slate-400 text-xs">City *</Label>
                        <Input 
                          id={'sat-office-city-' + office.office_id}
                          name="office_city"
                          value={office.city || ''} 
                          onChange={function(e) { updateSatelliteOffice(office.office_id, 'city', e.target.value); }} 
                          placeholder="Dallas" 
                          className="mt-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor={'sat-office-state-' + office.office_id} className="text-slate-400 text-xs">State *</Label>
                          <Input 
                            id={'sat-office-state-' + office.office_id}
                            name="office_state"
                            value={office.state || ''} 
                            onChange={function(e) { updateSatelliteOffice(office.office_id, 'state', e.target.value.toUpperCase()); }} 
                            maxLength={2}
                            placeholder="TX" 
                            className="mt-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                          />
                        </div>
                        <div>
                          <Label htmlFor={'sat-office-zip-' + office.office_id} className="text-slate-400 text-xs">ZIP *</Label>
                          <Input 
                            id={'sat-office-zip-' + office.office_id}
                            name="office_zip"
                            value={office.zip_code || ''} 
                            onChange={function(e) { updateSatelliteOffice(office.office_id, 'zip_code', e.target.value); }} 
                            placeholder="75201" 
                            className="mt-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor={'sat-office-phone-' + office.office_id} className="text-slate-400 text-xs">Phone (optional)</Label>
                        <Input 
                          id={'sat-office-phone-' + office.office_id}
                          name="office_phone"
                          value={office.phone || ''} 
                          onChange={function(e) { updateSatelliteOffice(office.office_id, 'phone', e.target.value); }} 
                          placeholder="(555) 123-4567" 
                          className="mt-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                        />
                      </div>
                      <div>
                        <Label htmlFor={'sat-office-contact-' + office.office_id} className="text-slate-400 text-xs">Contact Name (optional)</Label>
                        <Input 
                          id={'sat-office-contact-' + office.office_id}
                          name="office_contact"
                          value={office.contact_name || ''} 
                          onChange={function(e) { updateSatelliteOffice(office.office_id, 'contact_name', e.target.value); }} 
                          placeholder="John Smith" 
                          className="mt-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500" 
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      
      <Button onClick={updateProfile} className="btn-accent" disabled={saving}>
        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Profile
      </Button>
    </div>
  );
}

export default ProfileTab;
